<script setup lang="ts">
/**
 * AuraSidebar - Sidebar with conversation history
 * Single Responsibility: Display and manage conversation list
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChatAltIcon, ClockIcon, TrashIcon } from '@heroicons/vue/outline';

import { useAuraStore } from '@/stores/modules/aura';
import { can } from '@/composables/useAuth';
import { Permission } from '@/types';
import { formatRelativeDate } from '@/utils/datetime';
import type { AuraConversation } from '@/types';

const { t } = useI18n();
const store = useAuraStore();

// Props
defineProps<{
    isHistoryLoading: boolean;
}>();

// Emits
const emit = defineEmits<{
    select: [conversation: AuraConversation];
    create: [];
    delete: [conversation: AuraConversation];
}>();

// Permissions
const canViewHistory = computed(() => can(Permission.VIEW_AURA_CONVERSATION));
const canCreateConversation = computed(() => can(Permission.CREATE_AURA_CONVERSATION));
const canDeleteConversation = computed(() => can(Permission.DELETE_AURA_CONVERSATION));

// Computed from store
const hasConversations = computed(() => store.conversations.length > 0);
const conversations = computed(() => store.conversations);
const currentConversationId = computed(() => store.currentConversation?.id || null);
// Handlers
const selectConversation = (conversation: AuraConversation) => {
    emit('select', conversation);
};

const createConversation = () => {
    emit('create');
};

const deleteConversation = (conversation: AuraConversation) => {
    emit('delete', conversation);
};
</script>

<template>
    <aside
        v-if="canViewHistory"
        class="h-full w-[260px] flex-shrink-0 border-r border-[#E5E5E5] bg-[#DADDE2]"
    >
        <!-- Sidebar header -->
        <div class="flex flex-col gap-4 p-4 pt-6">
            <h1 class="px-2 text-xl font-bold text-gray-800">mobcall AURA™</h1>
            <button
                :disabled="!canCreateConversation"
                :class="[
                    'flex items-center justify-center rounded-xl px-4 py-3 text-white transition-colors',
                    canCreateConversation
                        ? 'bg-[#242B59] hover:bg-[#1a203f]'
                        : 'cursor-not-allowed bg-gray-400',
                ]"
                @click="createConversation"
            >
                <span class="font-semibold">{{ t('aura.chat.newChat') }}</span>
            </button>
        </div>

        <!-- Conversations list -->
        <div class="h-[calc(100%-140px)] overflow-y-auto px-4">
            <!-- Loading state -->
            <div v-if="isHistoryLoading" class="flex items-center justify-center py-8">
                <div
                    class="h-6 w-6 animate-spin rounded-full border-2 border-[#242B59] border-t-transparent"
                ></div>
            </div>

            <div v-else class="space-y-1">
                <p class="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
                    {{ t('aura.chat.history') }}
                </p>

                <button
                    v-for="conversation in conversations"
                    :key="conversation.id"
                    :class="[
                        'group flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                        currentConversationId === conversation.id
                            ? 'bg-white shadow-sm'
                            : 'hover:bg-white/50',
                    ]"
                    @click="selectConversation(conversation)"
                >
                    <ChatAltIcon
                        :class="[
                            'mt-0.5 h-4 w-4 flex-shrink-0',
                            currentConversationId === conversation.id
                                ? 'text-[#48DDC3]'
                                : 'text-[#242B59]',
                        ]"
                    />
                    <div class="min-w-0 flex-1">
                        <p
                            :class="[
                                'truncate text-sm font-medium',
                                currentConversationId === conversation.id
                                    ? 'text-[#242B59]'
                                    : 'text-[#242B59]',
                            ]"
                        >
                            {{ conversation.title || 'Untitled' }}
                        </p>
                        <p class="mt-0.5 truncate text-xs text-[#626468]">
                            {{ conversation.title || 'No messages' }}
                        </p>
                        <p class="mt-1 flex items-center gap-1 text-xs text-[#6B7280]">
                            <ClockIcon class="h-3 w-3" />
                            {{
                                formatRelativeDate(conversation.createdAt) || conversation.createdAt
                            }}
                        </p>
                    </div>

                    <!-- Delete button -->
                    <button
                        v-if="canDeleteConversation"
                        :class="[
                            'ml-1 flex-shrink-0 rounded p-1 opacity-0 transition-opacity',
                            'hover:bg-red-100 hover:text-red-600',
                            'group-hover:opacity-100',
                            'focus:opacity-100',
                        ]"
                        :title="t('aura.chat.deleteConversation')"
                        @click.stop="deleteConversation(conversation)"
                    >
                        <TrashIcon class="h-4 w-4" />
                    </button>
                </button>

                <div v-if="!hasConversations" class="px-2 py-4 text-center">
                    <p class="text-sm text-[#6B7280]">
                        {{ t('aura.conversation.noConversations') }}
                    </p>
                </div>
            </div>
        </div>
    </aside>
</template>
